// The leader's Buffers view: every buffer on the lines in scope, how full it
// is against its levels, and whether it was filled in sequence.
//
// Two groupings of one table. By buffer reads across the floor — "CNC > Area C
// Standing" on every line at once, which is what a supplier's leader wants. By
// flowline reads down a line — every buffer a pod on Flowline 2 passes
// through, which is what a line leader wants. The filter keeps only what needs
// somebody: a level below minimum, or pods that arrived out of production
// sequence.

import { useMemo, useState } from 'react';
import type { BufferLineVM } from '@/api/types';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';
import type { Translations } from '@/i18n/strings';
import { useBufferLines, useFlowlineShort, useFlowlines } from '@/queries';

export function bufferTone(state: BufferLineVM['state'], t: Translations): { colour: string; bg: string; label: string } {
  switch (state) {
    case 'critical':
      return { colour: 'var(--danger)', bg: 'var(--dangerSoft)', label: t.criticalT };
    case 'warning':
      return { colour: 'var(--warn)', bg: 'var(--warnSoft)', label: t.lowLevelT };
    case 'over_production':
      return { colour: '#2f6bff', bg: 'rgba(47,107,255,.12)', label: t.overT };
    default:
      return { colour: 'var(--pos)', bg: 'var(--posSoft)', label: t.inRangeT };
  }
}

const isLow = (b: BufferLineVM) => b.state === 'critical' || b.state === 'warning';
const outOfSeq = (b: BufferLineVM) => Math.max(0, b.filled - b.inSequenceCount);

export function FloorBuffers({ flowlineIds }: { flowlineIds: number[] }) {
  const t = useT();
  const lines = useBufferLines();
  const flowlines = useFlowlines();
  const short = useFlowlineShort();
  const [groupBy, setGroupBy] = useState<'buffer' | 'flowline'>('buffer');
  const [onlyBad, setOnlyBad] = useState(false);

  const rows = useMemo(
    () =>
      (lines.data ?? [])
        .filter((b) => flowlineIds.includes(b.flowlineId))
        .filter((b) => !onlyBad || isLow(b) || outOfSeq(b) > 0),
    [lines.data, flowlineIds, onlyBad],
  );

  const groups = useMemo(() => {
    if (groupBy === 'buffer') {
      const byBuffer = new Map<string, { name: string; rows: BufferLineVM[] }>();
      for (const b of rows) {
        const key = String(b.bufferId ?? b.bufferName);
        const g = byBuffer.get(key) ?? { name: b.bufferName, rows: [] };
        g.rows.push(b);
        byBuffer.set(key, g);
      }
      return [...byBuffer.values()].map((g) => ({
        name: g.name,
        rows: g.rows.sort((a, b) => short(a.flowlineId).localeCompare(short(b.flowlineId))),
        lead: (b: BufferLineVM) => short(b.flowlineId),
        title: (b: BufferLineVM) => `${b.inWorkcentreName} → ${b.outWorkcentreName}`,
        unit: t.linesT,
      }));
    }
    return (flowlines.data ?? [])
      .filter((f) => flowlineIds.includes(f.id))
      .map((f) => ({
        name: f.name,
        rows: rows.filter((b) => b.flowlineId === f.id),
        lead: () => '',
        title: (b: BufferLineVM) => b.bufferName,
        unit: t.buffersT.toLowerCase(),
      }))
      .filter((g) => g.rows.length > 0);
  }, [rows, groupBy, flowlines.data, flowlineIds, short, t]);

  const low = rows.filter(isLow).length;
  const oos = rows.filter((b) => outOfSeq(b) > 0).length;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', gap: 4, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 999, padding: 3 }}>
          {(
            [
              ['buffer', t.byBufferT],
              ['flowline', t.byFlowlineT],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              className="press"
              aria-pressed={groupBy === k}
              onClick={() => setGroupBy(k)}
              style={{
                borderRadius: 999,
                padding: '6px 12px',
                minHeight: 30,
                font: '700 11px Manrope,sans-serif',
                background: groupBy === k ? 'var(--ink)' : 'transparent',
                color: groupBy === k ? 'var(--bg)' : 'var(--sub)',
              }}
            >
              {label}
            </button>
          ))}
        </span>
        <button
          type="button"
          className="press"
          aria-pressed={onlyBad}
          onClick={() => setOnlyBad((v) => !v)}
          style={{
            borderRadius: 999,
            padding: '6px 12px',
            minHeight: 30,
            font: '700 11px Manrope,sans-serif',
            background: onlyBad ? 'var(--ink)' : 'var(--surface)',
            color: onlyBad ? 'var(--bg)' : 'var(--sub)',
            border: '1px solid var(--line)',
          }}
        >
          {t.lowAndOosOnlyT}
          {onlyBad ? ' ✓' : ''}
        </button>
        <span
          style={{
            font: '500 11.5px Manrope,sans-serif',
            color: 'var(--sub)',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {rows.length} {t.bufferLinesT} · {low} {t.lowCountT} · {oos} {t.outOfSequenceT}
        </span>
      </div>

      {lines.isPending ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 12 }}>
          <Skeleton height={180} radius={14} />
          <Skeleton height={180} radius={14} />
        </div>
      ) : groups.length === 0 ? (
        <div style={{ background: 'var(--surface2)', borderRadius: 13 }}>
          <EmptyState>{t.noBufferIntoT}</EmptyState>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 12, alignItems: 'start' }}>
          {groups.map((g) => {
            const bad = g.rows.filter(isLow).length;
            const seq = g.rows.filter((b) => outOfSeq(b) > 0).length;
            return (
              <div
                key={g.name}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                  borderRadius: 14,
                  padding: '8px 14px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minHeight: 36 }}>
                  <span style={{ font: "700 15px 'Space Grotesk',sans-serif" }}>{g.name}</span>
                  <span style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)' }}>
                    {[`${g.rows.length} ${g.unit}`, bad ? `${bad} ${t.lowCountT}` : null, seq ? `${seq} ${t.outOfSeqShortT}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
                {g.rows.map((b) => {
                  const tone = bufferTone(b.state, t);
                  const pct = b.max ? Math.min(100, Math.round((b.filled / b.max) * 100)) : 0;
                  const minPct = b.max ? Math.round((b.min / b.max) * 100) : 0;
                  const oosCount = outOfSeq(b);
                  return (
                    <div key={b.id} style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '8px 0', borderTop: '1px solid var(--surface2)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {g.lead(b) && (
                          <span style={{ font: "700 10.5px 'Space Grotesk',sans-serif", color: 'var(--sub)', flex: 'none' }}>{g.lead(b)}</span>
                        )}
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            font: '600 12px Manrope,sans-serif',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {g.title(b)}
                        </span>
                        {oosCount > 0 && (
                          <span
                            style={{
                              borderRadius: 999,
                              padding: '2px 8px',
                              font: '700 10px Manrope,sans-serif',
                              background: 'var(--dangerSoft)',
                              color: 'var(--danger)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {oosCount} {t.outOfSequenceT}
                          </span>
                        )}
                        <span
                          style={{
                            borderRadius: 999,
                            padding: '2px 8px',
                            font: '700 10px Manrope,sans-serif',
                            background: tone.bg,
                            color: tone.colour,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {tone.label}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ flex: 1, height: 7, borderRadius: 4, background: 'var(--surface2)', overflow: 'hidden', position: 'relative' }}>
                          <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: tone.colour }} />
                          <span style={{ position: 'absolute', top: 0, bottom: 0, left: `${minPct}%`, width: 2, background: 'var(--ink)', opacity: 0.5 }} />
                        </span>
                        <span className="numeric" style={{ font: '600 11px Manrope,sans-serif', color: 'var(--sub)', whiteSpace: 'nowrap' }}>
                          {b.filled} / {b.max} · {t.minLevelT} {b.min} · {b.inSequenceCount} {t.inSeqT}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
